import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Send, MapPin, Handshake, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import { applyPartner } from "@/lib/api";

const PERKS = [
  { icon: TrendingUp, t: "Qualified leads", d: "Get website & digital leads auto-routed from your city and district." },
  { icon: Handshake, t: "Trusted brand", d: "Work under the Kerala.com brand with ready-made proposals & pricing." },
  { icon: Users, t: "Local ownership", d: "Own your territory — be the go-to digital partner for local businesses." },
];

export default function KeralaPartners() {
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", business_name: "", city: "", district: "",
    state: "Kerala", experience: "", message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { document.title = "Kerala.com · Become a Partner"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.city.trim()) {
      toast.error("Please add your name, email and city.");
      return;
    }
    setSaving(true);
    try { await applyPartner("kerala", form); setDone(true); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Couldn't submit. Please try again."); }
    setSaving(false);
  };

  const inputCls = "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-3 text-slate-900 outline-none focus:border-emerald-600 text-sm";

  return (
    <div data-testid="kerala-partners">
      <section className="bg-gradient-to-br from-[#024E3F] to-[#012a22] text-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-300">Partner Network</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-2">Become a Kerala.com city partner</h1>
          <p className="text-emerald-100/80 mt-4 md:text-lg max-w-2xl">We're building a network of local partners across every Kerala city and district. Bring the businesses — we bring the platform, brand and playbook.</p>
          <a href="/kerala/partner-portal" data-testid="partner-login-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-200 hover:text-white">Already a partner? Log in →</a>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {PERKS.map((p, i) => (
            <div key={i} className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-6">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center mb-4"><p.icon size={20} className="text-emerald-700" /></div>
              <h3 className="font-heading font-bold text-slate-900">{p.t}</h3>
              <p className="text-sm text-slate-500 mt-1.5">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {done ? (
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-10 text-center" data-testid="kerala-partner-success">
              <CheckCircle2 className="text-emerald-500 mx-auto" size={44} />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">Application received 🤝</h2>
              <p className="text-slate-500 mt-2">Thanks for your interest in becoming a Kerala.com partner. Our team will review and reach out about your territory.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 md:p-8 space-y-4" data-testid="kerala-partner-form">
              <div className="flex items-center gap-2"><MapPin size={18} className="text-emerald-700" /><h2 className="font-heading text-xl font-bold text-slate-900">Apply for your territory</h2></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name *" className={inputCls} data-testid="partner-name" />
                <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Company / agency" className={inputCls} data-testid="partner-business" />
                <input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" type="email" className={inputCls} data-testid="partner-email" />
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone / WhatsApp" className={inputCls} data-testid="partner-phone" />
                <input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City *" className={inputCls} data-testid="partner-city" />
                <input value={form.district} onChange={(e) => set("district", e.target.value)} placeholder="District" className={inputCls} data-testid="partner-district" />
              </div>
              <input value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="Your experience (web, marketing, sales…)" className={inputCls} data-testid="partner-experience" />
              <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} placeholder="Tell us why you'd be a great local partner" className={`${inputCls} resize-none`} data-testid="partner-message" />
              <button type="submit" disabled={saving} data-testid="partner-submit"
                className="w-full bg-emerald-700 text-white font-semibold rounded-xl px-5 py-3.5 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} /> Submit application</>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
